import React, { useEffect, useState } from 'react';

import { Alert, Box, Tabs } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import TabBadge from 'common/components/TabBadge';

import {
    getHasBagEnergyField,
    handleOnChangeEnergyFields,
} from '../../helpers';
import * as selectors from '../../selectors';

import FormBottomActions from './FormBottomActions';
import PeriodFields from './PeriodFields';

const EnergyStep = ({
    availableBag,
    compensationScheme,
    control,
    disabled,
    disabledNext,
    energyBalanceErrors,
    getValues,
    handleChangeAvailableBag,
    handleClickBack,
    handleClickNext,
    rate,
    setEnergyBalanceErrors,
    setValue,
}) => {
    const [selectedTier, setSelectedTier] = useState(null);
    const { t } = useTranslation();
    const [fieldsKeys, tierLabels] = useWatch({
        control,
        name: ['fields_keys', 'tier_labels'],
    });

    useEffect(
        () => setSelectedTier(fieldsKeys?.energy?.[0] || null),
        [fieldsKeys?.energy],
    );

    const handleOnChangePeriodField = (key) => {
        handleOnChangeEnergyFields({
            getValues,
            key,
            setEnergyBalanceErrors,
            setValue,
        });

        const hasBagEnergyField = getHasBagEnergyField(
            rate,
            compensationScheme,
        );

        if (hasBagEnergyField) handleChangeAvailableBag(availableBag);
    };

    return (
        <>
            {fieldsKeys?.energy?.length > 0 && selectedTier && (
                <Box
                    alignItems="center"
                    borderBottom={1}
                    borderColor="divider"
                    display="flex"
                    justifyContent="center"
                    mb={3}
                >
                    <Tabs
                        onChange={(_, newTab) => setSelectedTier(newTab)}
                        scrollButtons="auto"
                        value={selectedTier}
                        variant="scrollable"
                    >
                        {fieldsKeys.energy.map((tier) => (
                            <TabBadge
                                key={tier}
                                label={tierLabels?.energy?.[tier] || tier}
                                value={tier}
                                visible={
                                    energyBalanceErrors?.hasErrors &&
                                    Object.values(
                                        energyBalanceErrors?.errors?.[tier],
                                    ).some(Boolean)
                                }
                            />
                        ))}
                    </Tabs>
                </Box>
            )}

            <PeriodFields
                baseName={selectedTier ? `energy.${selectedTier}` : 'total'}
                compensationScheme={compensationScheme}
                control={control}
                disabled={disabled}
                errors={
                    selectedTier
                        ? energyBalanceErrors?.errors?.[selectedTier]
                        : energyBalanceErrors?.errors?.total
                }
                key={selectedTier || 'total'}
                onChangeEnergyConcept={() =>
                    handleOnChangePeriodField(selectedTier || '')
                }
            />

            {energyBalanceErrors?.hasErrors && (
                <Alert severity="error">
                    {t(
                        'There are errors in the energy balance. Please review it',
                    )}
                </Alert>
            )}

            <FormBottomActions
                disabled={disabled || disabledNext}
                handleClickBack={handleClickBack}
                handleClickNext={handleClickNext}
                nextText={t('Save')}
                type="submit"
            />
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    availableBag: selectors.getAvailableBag,
});

EnergyStep.propTypes = {
    availableBag: PropTypes.number,
    compensationScheme: PropTypes.string,
    control: PropTypes.object,
    disabled: PropTypes.bool,
    disabledNext: PropTypes.bool,
    energyBalanceErrors: PropTypes.object,
    getValues: PropTypes.func,
    handleChangeAvailableBag: PropTypes.func,
    handleClickBack: PropTypes.func,
    handleClickNext: PropTypes.func,
    rate: PropTypes.object,
    setEnergyBalanceErrors: PropTypes.func,
    setValue: PropTypes.func,
};

export default connect(mapStateToProps)(EnergyStep);
